import { ChevronUp, ChevronDown, Check, X, Trash2, ImagePlus } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { StarRatingDisplay } from './StarRatingDisplay'
import type { TestimonialDocument } from '@/types'

interface AdminTestimonialRowProps {
  testimonial: TestimonialDocument & { id: string }
  onApprove?: () => void
  onReject?: () => void
  onDelete?: () => void
  onAddPhoto?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  isFirst?: boolean
  isLast?: boolean
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function formatDate(value: unknown): string {
  if (!value) return '—'
  try {
    const date =
      value instanceof Date
        ? value
        : typeof value === 'object' && value !== null && 'toDate' in value
          ? (value as { toDate(): Date }).toDate()
          : new Date(String(value))
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return '—'
  }
}

export function AdminTestimonialRow({
  testimonial,
  onApprove,
  onReject,
  onDelete,
  onAddPhoto,
  onMoveUp,
  onMoveDown,
  isFirst = false,
  isLast = false,
}: AdminTestimonialRowProps) {
  const {
    clientName,
    clientTitle,
    serviceType,
    content,
    rating,
    photoUrl,
    source,
    createdAt,
  } = testimonial

  return (
    <TableRow>
      {/* Avatar + Name */}
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar>
            {photoUrl ? <AvatarImage src={photoUrl} alt={clientName} /> : null}
            <AvatarFallback>{getInitials(clientName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium leading-tight">{clientName}</p>
            {clientTitle && (
              <p className="text-xs text-muted-foreground">{clientTitle}</p>
            )}
            {serviceType && (
              <Badge variant="outline" className="mt-0.5 text-xs">
                {serviceType}
              </Badge>
            )}
          </div>
        </div>
      </TableCell>

      {/* Rating */}
      <TableCell>
        <StarRatingDisplay rating={rating} size="sm" />
      </TableCell>

      {/* Content preview */}
      <TableCell className="max-w-xs">
        <p className="line-clamp-2 text-sm text-muted-foreground">{content}</p>
      </TableCell>

      {/* Source */}
      <TableCell>
        <Badge variant={source === 'survey' ? 'default' : 'secondary'} className="text-xs">
          {source === 'survey' ? 'Survey' : 'Manual'}
        </Badge>
      </TableCell>

      {/* Date */}
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        {formatDate(createdAt)}
      </TableCell>

      {/* Actions */}
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          {onMoveUp && (
            <Button
              variant="ghost"
              size="sm"
              disabled={isFirst}
              onClick={onMoveUp}
              aria-label="Move up"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          )}
          {onMoveDown && (
            <Button
              variant="ghost"
              size="sm"
              disabled={isLast}
              onClick={onMoveDown}
              aria-label="Move down"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          )}
          {onAddPhoto && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddPhoto}
              aria-label="Upload photo"
              title="Upload client photo"
            >
              <ImagePlus className="h-4 w-4" />
            </Button>
          )}
          {onApprove && (
            <Button
              variant="ghost"
              size="sm"
              className="text-green-600 hover:text-green-700"
              onClick={onApprove}
              aria-label="Approve"
              title="Approve testimonial"
            >
              <Check className="h-4 w-4" />
            </Button>
          )}
          {onReject && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={onReject}
              aria-label="Reject"
              title="Reject testimonial"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete"
              title="Delete testimonial"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
